/**
 * PaymentsList — PAY-02 (2026-08-06; reshaped 2026-08-07). Payments LIVE ON their
 * invoices now (Seth: "I want to see what invoice that payment is applied to
 * easily"):
 *   · InvoicePaymentRows — each invoice's payments render as sub-rows directly
 *     under the invoice line in the 360 (date · method · auth · status · amount,
 *     with fee / split / refund detail beneath).
 *   · OtherPaymentsCard — catches payments NOT tied to a visible Pegasus invoice
 *     (ESC-invoice payments later, deleted invoices) so money never disappears.
 *   · RefundModal — full/partial refunds (reason required, audited) + attached
 *     documentation (PAY-02c: email correspondence etc., stored with the refund
 *     via pegasus_file.refund_id — also visible in the customer's Documents tab).
 * The LEDGER underneath is unchanged: payment → allocations → invoices. One
 * payment can cover several invoices; one invoice can be paid in stages.
 */
import { useRef, useState } from "react";
import { format, parseISO } from "date-fns";
import { useQueryClient } from "@tanstack/react-query";
import {
  CreditCard, Banknote, Landmark, ExternalLink, Undo2, CornerDownRight, Paperclip, FileText, X,
} from "lucide-react";
import { Card } from "@/components/ui/Card";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { InfoTip } from "@/components/InfoTip";
import { uploadFilesTo, prettySize, MAX_BYTES, type PegasusFile } from "@/features/files/filesCore";
import {
  METHOD_LABEL, cardBrandLabel, moneyExact, useRefundPayment, type PegasusPayment,
} from "./payments";

const REASONS: { value: string; label: string }[] = [
  { value: "requested_by_customer", label: "Customer Asked" },
  { value: "duplicate", label: "Duplicate Charge" },
  { value: "service_issue", label: "Service Issue" },
  { value: "billing_error", label: "Billing Error" },
  { value: "goodwill", label: "Goodwill Credit" },
  { value: "other", label: "Other" },
];
const reasonLabel = (value: string) => REASONS.find((x) => x.value === value)?.label ?? value;

function statusPill(p: PegasusPayment) {
  const map: Record<string, { cls: string; label: string }> = {
    succeeded: { cls: "bg-emerald-100 text-emerald-700", label: "Paid" },
    recorded: { cls: "bg-emerald-100 text-emerald-700", label: "Recorded" },
    processing: { cls: "bg-brand-50 text-brand-700", label: "Processing" },
    pending: { cls: "bg-slate-100 text-slate-500", label: "Pending" },
    failed: { cls: "bg-red-100 text-red-700", label: "Failed" },
    canceled: { cls: "bg-slate-100 text-slate-500", label: "Canceled" },
    refunded: { cls: "bg-violet-100 text-violet-700", label: "Refunded" },
    partially_refunded: { cls: "bg-violet-100 text-violet-700", label: "Partly Refunded" },
    void: { cls: "bg-slate-100 text-slate-500", label: "Void" },
  };
  const s = map[p.status] ?? { cls: "bg-slate-100 text-slate-500", label: p.status };
  return <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${s.cls}`}>{s.label}</span>;
}

function methodIcon(m: string) {
  if (m === "check") return <Landmark className="h-3.5 w-3.5 text-slate-400" />;
  if (m === "cash") return <Banknote className="h-3.5 w-3.5 text-slate-400" />;
  return <CreditCard className="h-3.5 w-3.5 text-slate-400" />;
}

function methodText(p: PegasusPayment) {
  if (p.method === "card" || p.method === "card_on_file")
    return `${cardBrandLabel(p.card_brand)}${p.card_last4 ? ` ····${p.card_last4}` : ""}${p.method === "card_on_file" ? " · on file" : ""}`;
  // PAY-03: ACH rows carry the bank name/last4 in the brand/last4 slots.
  if (p.method === "ach")
    return `${p.card_brand || "Bank"}${p.card_last4 ? ` ····${p.card_last4}` : ""} · bank payment`;
  return `${METHOD_LABEL[p.method] ?? p.method}${p.reference ? ` #${p.reference}` : ""}`;
}

/** stripe+pending = a portal checkout that was opened but never confirmed — no
 *  money moved, no balance held. Hidden everywhere; it flips to 'processing'
 *  the instant the customer confirms (webhook + portal-pay finalize). */
const isAbandonedIntent = (p: PegasusPayment) => p.provider === "stripe" && p.status === "pending";

function refundableAmount(p: PegasusPayment) {
  const done = (p.pegasus_payment_refund ?? []).filter((r) => r.status !== "failed").reduce((s, r) => s + Number(r.amount), 0);
  return Number(p.amount) + Number(p.surcharge_amount ?? 0) - done;
}
const canTakeRefund = (p: PegasusPayment) =>
  refundableAmount(p) > 0.005 && ["succeeded", "recorded", "partially_refunded"].includes(p.status);

/** Chip for one piece of refund documentation — opens the file (signed URL). */
function fileChip(f: PegasusFile, urls?: Map<string, string>) {
  const url = urls?.get(f.storage_path);
  const cls = "inline-flex max-w-[18rem] items-center gap-1 rounded-full border border-violet-200 bg-white px-2 py-0.5 text-[10.5px] font-medium text-violet-700 transition-colors hover:bg-violet-50";
  const inner = (
    <>
      <Paperclip className="h-3 w-3 shrink-0" />
      <span className="truncate">{f.filename}</span>
    </>
  );
  const title = `${f.filename}${f.size_bytes ? ` · ${prettySize(f.size_bytes)}` : ""}`;
  return url
    ? <a key={f.id} href={url} target="_blank" rel="noreferrer" className={cls} title={title}>{inner}</a>
    : <span key={f.id} className={cls} title={title}>{inner}</span>;
}

/**
 * Payment sub-rows for ONE invoice — rendered directly under that invoice's row
 * in the 360 list, so "what paid this?" needs zero clicks. Amount shown is what
 * was APPLIED to this invoice; fee / split / refund detail sits beneath it.
 */
export function InvoicePaymentRows({ invoiceId, payments, canRefund, onRefund, refundFiles, fileUrls }: {
  invoiceId: string;
  payments: PegasusPayment[];
  canRefund: boolean;
  onRefund: (p: PegasusPayment) => void;
  /** refund_id → uploaded documentation (PAY-02c) */
  refundFiles?: Map<string, PegasusFile[]>;
  fileUrls?: Map<string, string>;
}) {
  const entries = (payments ?? [])
    .filter((p) => p.status !== "void" && !isAbandonedIntent(p))
    .flatMap((p) => (p.pegasus_payment_allocation ?? [])
      .filter((a) => a.invoice_id === invoiceId)
      .map((a) => ({ p, a })));
  if (entries.length === 0) return null;

  return (
    <>
      {entries.map(({ p, a }) => {
        const allocs = p.pegasus_payment_allocation ?? [];
        const gross = allocs.reduce((s, x) => s + Number(x.amount), 0) || 1;
        const share = Number(a.amount) / gross;
        const fee = Number(p.surcharge_amount ?? 0);
        const feeShare = fee * share;
        const split = allocs.length > 1;
        const charged = Number(p.amount) + fee;
        const refunds = (p.pegasus_payment_refund ?? []).filter((r) => r.status === "succeeded");
        const disputes = p.pegasus_payment_dispute ?? [];
        return (
          <li key={a.id} className="bg-slate-50/60 py-2 pl-6 pr-4">
            <div className="flex items-center gap-3">
              <CornerDownRight className="h-3.5 w-3.5 shrink-0 text-slate-300" />
              <span className="w-24 shrink-0 text-xs tabular-nums text-slate-500">
                {format(parseISO(p.created_at), "MMM d, yyyy")}
              </span>
              <span className="flex min-w-0 flex-1 items-center gap-1.5 truncate text-xs text-slate-600">
                {methodIcon(p.method)}
                <span className="truncate">
                  {methodText(p)}
                  {p.auth_code ? <> · Auth <span className="font-mono">{p.auth_code}</span></> : null}
                  {p.provider === "stripe" && !p.livemode ? " · test" : ""}
                </span>
                {statusPill(p)}
              </span>
              {p.receipt_url && (
                <a href={p.receipt_url} target="_blank" rel="noreferrer" title="Stripe receipt"
                  className="shrink-0 rounded p-1 text-slate-300 transition-colors hover:bg-white hover:text-slate-600">
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              )}
              <span className="shrink-0 text-sm font-semibold tabular-nums text-slate-800">
                {moneyExact(Number(a.amount))}
              </span>
              {canRefund && canTakeRefund(p) && (
                <button type="button" onClick={() => onRefund(p)} title="Refund some or all of this payment"
                  className="shrink-0 rounded-md p-1.5 text-slate-300 transition-colors hover:bg-violet-50 hover:text-violet-600">
                  <Undo2 className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            {(feeShare > 0.005 || split || refunds.length > 0 || disputes.length > 0 || p.failure_reason || (p.method === "ach" && p.status === "processing")) && (
              <div className="mt-1 space-y-0.5 pl-[8.4rem] text-[11px] leading-relaxed text-slate-400">
                {p.method === "ach" && p.status === "processing" && (
                  <div>Bank payment clearing — usually 3–5 business days; the balance is held meanwhile</div>
                )}
                {/* PAY-04: chargebacks surface in red, right on the payment they hit */}
                {disputes.map((d) => (
                  <div key={d.id} className="font-medium text-red-600">
                    Disputed {moneyExact(Number(d.amount))} — {(d.reason ?? "chargeback").replace(/_/g, " ")} · {d.status.replace(/_/g, " ")}
                    {d.evidence_due_by ? ` · respond by ${format(parseISO(d.evidence_due_by), "MMM d")}` : ""}
                  </div>
                ))}
                {feeShare > 0.005 && (
                  <div>
                    {split
                      ? <>Includes {moneyExact(feeShare)} processing fee share</>
                      : <>Card charged {moneyExact(charged)} — {moneyExact(Number(a.amount))} applied + {moneyExact(fee)} processing fee</>}
                  </div>
                )}
                {split && <div>Part of a {moneyExact(charged)} payment across {allocs.length} invoices</div>}
                {refunds.map((r) => {
                  const rShare = Number(r.amount) * share;
                  const files = refundFiles?.get(r.id) ?? [];
                  return (
                    <div key={r.id} className="text-violet-600">
                      Refunded {moneyExact(rShare)}
                      {split ? ` (share of a ${moneyExact(Number(r.amount))} refund)` : ""}
                      {" — "}{reasonLabel(r.reason)}{r.note ? ` (${r.note})` : ""}
                      {" · "}{format(parseISO(r.created_at), "MMM d")}
                      {files.length > 0 && (
                        <span className="ml-1.5 inline-flex flex-wrap items-center gap-1 align-middle">
                          {files.map((f) => fileChip(f, fileUrls))}
                        </span>
                      )}
                    </div>
                  );
                })}
                {p.failure_reason && <div className="text-red-600">{p.failure_reason}</div>}
              </div>
            )}
          </li>
        );
      })}
    </>
  );
}

/**
 * Payments that don't sit under any invoice row above — ESC-invoice payments
 * (PAY-04 reserve) or allocations to an invoice that's no longer on file. Kept
 * visible so no payment can silently vanish from the money story. Renders
 * nothing when (as usual) every payment nests under its invoice.
 */
export function OtherPaymentsCard({ payments, knownInvoiceIds, canRefund, onRefund }: {
  payments: PegasusPayment[];
  knownInvoiceIds: Set<string>;
  canRefund: boolean;
  onRefund: (p: PegasusPayment) => void;
}) {
  const rows = (payments ?? []).filter((p) =>
    p.status !== "void" && !isAbandonedIntent(p) &&
    !(p.pegasus_payment_allocation ?? []).some((a) => a.invoice_id && knownInvoiceIds.has(a.invoice_id)));
  if (rows.length === 0) return null;

  return (
    <Card className="mt-3 overflow-hidden p-0">
      <div className="flex items-center gap-2 border-b border-hairline px-4 py-2.5">
        <CreditCard className="h-4 w-4 text-slate-400" />
        <h3 className="text-sm font-semibold text-slate-900">Other Payments</h3>
        <InfoTip label="About other payments">Payments that aren&apos;t tied to an invoice above — for example, one applied to an ESC invoice or held for an install job.</InfoTip>
      </div>
      <ul className="divide-y divide-hairline">
        {rows.map((p) => {
          const refunds = (p.pegasus_payment_refund ?? []).filter((r) => r.status === "succeeded");
          const disputes = p.pegasus_payment_dispute ?? [];
          const allocs = p.pegasus_payment_allocation ?? [];
          return (
            <li key={p.id} className="px-4 py-2.5">
              <div className="flex items-center gap-3">
                <span className="w-24 shrink-0 text-xs tabular-nums text-slate-500">
                  {format(parseISO(p.created_at), "MMM d, yyyy")}
                </span>
                <span className="flex min-w-0 flex-1 items-center gap-1.5 truncate text-xs text-slate-600">
                  {methodIcon(p.method)}
                  <span className="truncate">
                    {methodText(p)}
                    {p.auth_code ? <> · Auth <span className="font-mono">{p.auth_code}</span></> : null}
                    {p.provider === "stripe" && !p.livemode ? " · test" : ""}
                  </span>
                  {statusPill(p)}
                </span>
                {p.receipt_url && (
                  <a href={p.receipt_url} target="_blank" rel="noreferrer" title="Stripe receipt"
                    className="shrink-0 rounded p-1 text-slate-300 transition-colors hover:bg-slate-50 hover:text-slate-600">
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                )}
                <span className="shrink-0 text-sm font-semibold tabular-nums text-slate-800">
                  {moneyExact(Number(p.amount) + Number(p.surcharge_amount ?? 0))}
                </span>
                {canRefund && canTakeRefund(p) && (
                  <button type="button" onClick={() => onRefund(p)} title="Refund some or all of this payment"
                    className="shrink-0 rounded-md p-1.5 text-slate-300 transition-colors hover:bg-violet-50 hover:text-violet-600">
                    <Undo2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
              {(allocs.length > 0 || Number(p.surcharge_amount) > 0 || refunds.length > 0 || disputes.length > 0 || p.failure_reason) && (
                <div className="mt-1 pl-[6.75rem] text-[11px] text-slate-400">
                  {disputes.map((d) => (
                    <span key={d.id} className="font-medium text-red-600">
                      Disputed {moneyExact(Number(d.amount))} — {(d.reason ?? "chargeback").replace(/_/g, " ")} · {d.status.replace(/_/g, " ")}
                      {d.evidence_due_by ? ` · respond by ${format(parseISO(d.evidence_due_by), "MMM d")}` : ""} ·{" "}
                    </span>
                  ))}
                  {allocs.map((a) => (
                    <span key={a.id}>
                      Applied {moneyExact(Number(a.amount))}
                      {a.esc_invoice_no ? ` to ESC #${a.esc_invoice_no}` : a.invoice_id ? " to an invoice no longer on file" : a.job_id ? " — held for an install job until it closes" : ""} ·{" "}
                    </span>
                  ))}
                  {Number(p.surcharge_amount) > 0 && <>includes {moneyExact(Number(p.surcharge_amount))} processing fee · </>}
                  {refunds.map((r) => (
                    <span key={r.id} className="text-violet-600">
                      refunded {moneyExact(Number(r.amount))} — {reasonLabel(r.reason)}
                      {r.note ? ` (${r.note})` : ""} ·{" "}
                    </span>
                  ))}
                  {p.failure_reason && <span className="text-red-600">{p.failure_reason}</span>}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

/**
 * Refund a payment — money surface → document scale (Seth 2026-08-07: "make it
 * much larger"). PAY-02c: documentation (the email thread, photos) can be
 * attached; the refund runs FIRST, then files upload against its refund_id —
 * if the upload trips, the refund is safe and "Attach files" retries just that.
 */
export function RefundModal({ target, custno, onClose }: {
  target: PegasusPayment;
  custno: string;
  onClose: () => void;
}) {
  const refund = useRefundPayment(custno);
  const qc = useQueryClient();
  const max = refundableAmount(target);
  const [amt, setAmt] = useState(max.toFixed(2));
  const [reason, setReason] = useState("requested_by_customer");
  const [note, setNote] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [oversize, setOversize] = useState<string | null>(null);
  const [doneRefundId, setDoneRefundId] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const pickRef = useRef<HTMLInputElement>(null);

  const busy = refund.isPending || uploading;
  const lockInputs = busy || !!doneRefundId;

  const pick = (list: FileList | null) => {
    if (!list) return;
    const ok: File[] = [];
    const big: string[] = [];
    for (const f of Array.from(list)) {
      if (f.size > MAX_BYTES) big.push(f.name);
      else ok.push(f);
    }
    if (ok.length) setFiles((prev) => [...prev, ...ok]);
    setOversize(big.length ? `${big.join(", ")} — over the 25 MB limit, not added.` : null);
    if (pickRef.current) pickRef.current.value = "";
  };

  const attach = async (refundId: string) => {
    setUploading(true);
    setErr(null);
    try {
      await uploadFilesTo(
        { custno, refund_id: refundId, caption: `Refund documentation — ${reasonLabel(reason)}` },
        files,
      );
      qc.invalidateQueries({ queryKey: ["customer_files"] });
      qc.invalidateQueries({ queryKey: ["payments", custno] });
      setUploading(false);
      onClose();
    } catch (e) {
      setUploading(false);
      setErr(
        `The refund went through, but attaching the files failed: ${(e as Error).message}. ` +
        `Click "Attach files" to retry — the refund will NOT run twice.`,
      );
    }
  };

  const go = () => {
    setErr(null);
    if (doneRefundId) { void attach(doneRefundId); return; }
    refund.mutate(
      { payment_id: target.id, amount: Number(amt), reason, note: note || undefined },
      {
        onSuccess: (res) => {
          const rid = res?.refund_id ?? null;
          if (files.length > 0 && rid) { setDoneRefundId(rid); void attach(rid); }
          else onClose();
        },
        onError: (e) => setErr((e as Error).message),
      },
    );
  };

  return (
    <Modal open onClose={() => !busy && onClose()} size="md"
      title={target.provider === "stripe" ? (
        <span className="flex items-center gap-2">
          Refund Payment
          <InfoTip label="How card refunds reach the customer">The money goes back through Stripe to the customer&apos;s card automatically. They&apos;ll see it in 5–10 business days.</InfoTip>
        </span>
      ) : "Refund Payment"}
      subtitle={`${methodText(target)} · ${moneyExact(max)} available to refund`}
      footer={
        <div className="flex items-center justify-end gap-3">
          <Button variant="ghost" size="lg" onClick={onClose} disabled={busy}>
            {doneRefundId ? "Close without files" : "Cancel"}
          </Button>
          <Button
            size="lg"
            disabled={busy || (!doneRefundId && !(Number(amt) > 0))}
            onClick={go}
          >
            {refund.isPending ? "Refunding…"
              : uploading ? "Attaching files…"
              : doneRefundId ? "Attach files"
              : `Refund ${moneyExact(Number(amt) || 0)}`}
          </Button>
        </div>
      }>
      <div className="space-y-5 py-1 text-[15px] leading-relaxed">
        <div>
          <label className="mb-1.5 block text-[13px] font-semibold text-slate-500">Amount to refund</label>
          <div className="relative w-48">
            <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-base text-slate-400">$</span>
            <input type="number" min="0.01" step="0.01" value={amt} onChange={(e) => setAmt(e.target.value)} disabled={lockInputs}
              className="w-full rounded-lg border border-hairline bg-white py-2.5 pl-7 pr-3 text-right text-xl font-semibold tabular-nums text-slate-900 focus:outline-none focus:ring-2 focus:ring-brand-500/30 disabled:bg-slate-50 disabled:text-slate-400" />
          </div>
          <p className="mt-1 text-[13px] text-slate-400">Up to {moneyExact(max)} — partial refunds are fine.</p>
        </div>
        <div>
          <label className="mb-1.5 block text-[13px] font-semibold text-slate-500">Reason</label>
          <select value={reason} onChange={(e) => setReason(e.target.value)} disabled={lockInputs}
            className="w-64 rounded-lg border border-hairline bg-white px-3 py-2.5 text-[15px] disabled:bg-slate-50 disabled:text-slate-400">
            {REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
        </div>
        <div>
          <label className="mb-1.5 block text-[13px] font-semibold text-slate-500">
            Note <span className="font-normal text-slate-400">(optional — shows on the ledger)</span>
          </label>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Why this refund is happening…" disabled={lockInputs}
            className="h-24 w-full rounded-lg border border-hairline bg-white px-3.5 py-2.5 text-[15px] disabled:bg-slate-50 disabled:text-slate-400" />
        </div>
        <div>
          <div className="mb-1.5 flex items-center gap-1.5">
            <label className="text-[13px] font-semibold text-slate-500">
              Documentation <span className="font-normal text-slate-400">(optional — the email thread, photos, anything worth keeping)</span>
            </label>
            <InfoTip label="Where the files are saved">Saved with this refund and in the customer&apos;s Documents, so the reason is on file if it ever comes up again.</InfoTip>
          </div>
          <input ref={pickRef} type="file" multiple className="hidden" onChange={(e) => pick(e.target.files)} />
          {files.length > 0 && (
            <ul className="mb-2 space-y-1">
              {files.map((f, i) => (
                <li key={`${f.name}-${i}`} className="flex items-center gap-2 rounded-lg border border-hairline bg-slate-50 px-3 py-2 text-[13px] text-slate-600">
                  <FileText className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                  <span className="min-w-0 flex-1 truncate">{f.name}</span>
                  <span className="shrink-0 text-slate-400">{prettySize(f.size)}</span>
                  {!doneRefundId && (
                    <button type="button" onClick={() => setFiles(files.filter((_, j) => j !== i))} disabled={busy}
                      className="shrink-0 rounded p-0.5 text-slate-300 transition-colors hover:bg-white hover:text-slate-600">
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
          <Button variant="ghost" size="sm" onClick={() => pickRef.current?.click()} disabled={busy || !!doneRefundId}>
            <Paperclip className="h-3.5 w-3.5" /> Add files
          </Button>
          {oversize && <p className="mt-1 text-[13px] font-medium text-amber-700">{oversize}</p>}
        </div>
        {target.provider !== "stripe" && (
          <p className="rounded-lg bg-slate-50 px-4 py-3 text-[13px] leading-relaxed text-slate-500">
            Check/cash refund: this records the credit on the ledger — hand the money back however you actually do it.
          </p>
        )}
        {err && <p className="rounded-lg bg-red-50 px-4 py-3 text-[13px] font-medium text-red-700">{err}</p>}
      </div>
    </Modal>
  );
}
